"use client";
// Imports
import Link from "next/link";
import { Container, ComponentWrapper, Img } from "@/components";
import { buttonVariants } from "@/components/ui/button";
import { buttonColourStyle, cn, resolveLink } from "@/lib/utils";

const STORY_BUTTON_COLORS = {
  background: "#892102",
  foreground: "#FFFFFF",
};

const SPACING_TOP = {
  none: "pt-0",
  small: "pt-3 lg:pt-4",
  medium: "pt-6 lg:pt-10",
  large: "pt-10 lg:pt-16",
  extra_large: "pt-16 lg:pt-24",
};

const SPACING_BOTTOM = {
  none: "pb-0",
  small: "pb-3 lg:pb-4",
  medium: "pb-6 lg:pb-10",
  large: "pb-10 lg:pb-16",
  extra_large: "pb-16 lg:pb-24",
};

function resolveSpacingValue(spacing) {
  if (!spacing) return "medium";
  return typeof spacing === "object" ? spacing.value : spacing;
}

function linkLabelText(label) {
  if (!label) return "";
  // Preserve CMS casing; only strip a trailing arrow we add in the UI
  return label.trim().replace(/\s*(→|‣|›|->)\s*$/, "");
}

function ArrowLinkLabel({ label }) {
  const text = linkLabelText(label);
  if (!text) return null;

  return (
    <>
      {text}{" "}
      <span
        aria-hidden
        className="inline-block transition-transform duration-200 ease-out group-hover:translate-x-1.5"
      >
        →
      </span>
    </>
  );
}

function StoryLink({ href, className, children }) {
  const link = resolveLink(href);
  if (!link) {
    return <div className={cn(className, "pointer-events-none")}>{children}</div>;
  }
  if (link.includes("http")) {
    return (
      <a href={link} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link href={link} className={className}>
      {children}
    </Link>
  );
}

function StoryCard({ story }) {
  const buttonLabel = story?.button_label?.trim() || "Read the full story";

  return (
    <StoryLink
      href={story?.link}
      className="group flex flex-col lg:flex-row overflow-hidden rounded-lg bg-white"
    >
      {story?.image && (
        <div className="relative w-full lg:w-[34%] xl:w-[32%] shrink-0 aspect-[16/10] lg:aspect-auto lg:min-h-[16rem] xl:min-h-[18rem] overflow-hidden">
          <Img
            data={story.image}
            fill
            sizes="(max-width: 1024px) 100vw, 34vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      )}
      <div className="flex flex-col flex-1 justify-center p-6 sm:p-8 lg:p-10 xl:px-12">
        {story?.subtitle && (
          <p className="font-body uppercase text-brand-red-gum text-md lg:text-lg mb-2">
            {story.subtitle}
          </p>
        )}
        {story?.title && (
          <h3 className="font-heading font-heading-sentence text-brand-espresso text-lg lg:text-xl leading-tight text-pretty">
            {story.title}
          </h3>
        )}
        {story?.description && (
          <p className="font-body text-brand-espresso text-md lg:text-lg leading-snug mt-3 lg:mt-4 text-pretty max-w-3xl lg:max-w-none">
            {story.description}
          </p>
        )}
        <span
          className={cn(
            buttonVariants({ variant: "outline", size: "default" }),
            "mt-6 lg:mt-8 self-start pointer-events-none border-2 uppercase tracking-wide"
          )}
          style={buttonColourStyle(STORY_BUTTON_COLORS)}
        >
          {buttonLabel}
        </span>
      </div>
    </StoryLink>
  );
}

// Featured Stories — full-width horizontal cards for news/articles
export function FeaturedStories({
  type,
  stories = [],
  sectionTitle,
  linkLabel,
  link,
  spacingTop,
  spacingBottom,
}) {
  const items = stories.filter(
    (story) => story?.title || story?.image || story?.description
  );
  if (!items.length) return null;

  const sectionHref = resolveLink(link);
  const topKey = resolveSpacingValue(spacingTop);
  const bottomKey = resolveSpacingValue(spacingBottom);

  return (
    <ComponentWrapper
      type={type}
      className={cn(
        SPACING_TOP[topKey] ?? SPACING_TOP.medium,
        SPACING_BOTTOM[bottomKey] ?? SPACING_BOTTOM.medium
      )}
    >
      <Container>
        {(sectionTitle || (sectionHref && linkLabel)) && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6 lg:mb-8">
            {sectionTitle && (
              <h2 className="font-heading font-heading-sentence text-brand-espresso text-xl lg:text-[2.5rem] leading-none">
                {sectionTitle}
              </h2>
            )}
            {sectionHref && linkLabel && (
              <Link
                href={sectionHref}
                className="group font-body font-bold text-brand-red-gum text-lg leading-none shrink-0 hover:text-brand-orange transition-colors"
              >
                <ArrowLinkLabel label={linkLabel} />
              </Link>
            )}
          </div>
        )}

        <div className="flex flex-col gap-6 lg:gap-8">
          {items.map((story, index) => (
            <StoryCard key={story?.id ?? index} story={story} />
          ))}
        </div>
      </Container>
    </ComponentWrapper>
  );
}
